import { useEffect, useState } from 'react';

import TranslatedLabel from '~/common/components/TranslatedLabel/TranslatedLabel';
import type { WizardStepProps } from '~/features/integrations/partners/types';

import VehiclesTable from './VehiclesTable';
import type { VehicleSelectionConfig } from './vehicleSelection.types';

const noop = () => {};

const useSelectedCountFooter = (
  onFooterChange: WizardStepProps['onFooterChange'],
  count: number,
  total: number | undefined
) => {
  useEffect(() => {
    if (total === undefined) return;
    onFooterChange?.(
      <TranslatedLabel
        translationKey="catalog:eligibleVehicles.selectedVehicles"
        variables={{ count, total }}
        typographyVariant="body_medium"
        dataTestId="vehicle-selection-selected-count"
      />
    );
  }, [onFooterChange, count, total]);

  useEffect(() => () => onFooterChange?.(null), [onFooterChange]);
};

/** Builds a partner's read-only peek and interactive picker from one shared table config. */
export function createVehicleSelectionViews(config: VehicleSelectionConfig) {
  /** Opened from "View vehicles"; a read-only peek, nothing here can change the draft. */
  const ViewVehiclesSubView = ({ onValidityChange }: WizardStepProps) => {
    useEffect(() => {
      onValidityChange(true);
    }, [onValidityChange]);

    return (
      <VehiclesTable
        {...config}
        selectedVehicleIds={[]}
        onSelectionChange={noop}
        readOnly
      />
    );
  };

  /** Opened from "Enroll individual vehicles"; the selection here is the enrollment's vehicleIds. */
  const SelectVehiclesSubView = ({
    draft,
    onChange,
    onValidityChange,
    onFooterChange,
  }: WizardStepProps) => {
    const selectedVehicleIds = draft.vehicleSelection?.vehicleIds ?? [];
    const [total, setTotal] = useState<number>();
    useSelectedCountFooter(onFooterChange, selectedVehicleIds.length, total);

    useEffect(() => {
      onValidityChange(selectedVehicleIds.length > 0);
    }, [selectedVehicleIds.length, onValidityChange]);

    return (
      <VehiclesTable
        {...config}
        selectedVehicleIds={selectedVehicleIds}
        onTotalChange={setTotal}
        onSelectionChange={(vehicleIds) =>
          onChange({ vehicleSelection: { fleetIds: [], vehicleIds } })
        }
      />
    );
  };

  return { ViewVehiclesSubView, SelectVehiclesSubView };
}
